import { useRef, useMemo, useState } from 'react'
import { ReactFlowProvider } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import {
  CurecallModalContainer,
  CurecallDropdownButton,
  CurecallSelect,
  CurecallToggle,
  type CurecallSelectItem,
  ECurecallButtonAppearance,
  ECurecallSelectAppearance,
  ECurecallNotificationAppearance,
  GlobeIcon,
  AlertIcon,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useChatbotFlow } from '../chatbot.provider'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useNotification } from 'src/providers/notification.provider'
import { ELocale, type TPatientLocale } from 'src/types/locales'
import { ChatbotPreviewFlow, type TChatbotPreviewFlowHandle } from './chatbot-preview-flow.component'
import { type TPdfOrientation } from './chatbot-pdf-export.helper'

type TChatbotPreviewModalProps = {
  onClose: VoidFunction
}

export function ChatbotPreviewModal({ onClose }: Readonly<TChatbotPreviewModalProps>) {
  const { t } = useTranslation()
  const { chatbotName, followupId, nodes, edges } = useChatbotFlow()
  const { getFollowupById } = useCurrentProject()
  const { notify } = useNotification()
  const followupName = getFollowupById(followupId)?.name ?? ''

  const languageOptions = useMemo<CurecallSelectItem[]>(
    () => [
      { label: t('pages.configuration.followups.chatbot.preview.french'), value: ELocale.FR },
      { label: t('pages.configuration.followups.chatbot.preview.english'), value: ELocale.EN },
      { label: t('pages.configuration.followups.chatbot.preview.polski'), value: ELocale.PL },
      { label: t('pages.configuration.followups.chatbot.preview.spanish'), value: ELocale.ES },
    ],
    [t],
  )

  const orientationOptions = useMemo<CurecallSelectItem[]>(
    () => [
      {
        label: t('pages.configuration.followups.chatbot.preview.pdf-orientation-landscape'),
        value: 'landscape',
      },
      {
        label: t('pages.configuration.followups.chatbot.preview.pdf-orientation-portrait'),
        value: 'portrait',
      },
    ],
    [t],
  )

  const [selectedLanguage, setSelectedLanguage] = useState<CurecallSelectItem>(languageOptions[0]!)
  const [selectedOrientation, setSelectedOrientation] = useState<CurecallSelectItem>(orientationOptions[0]!)
  const [autoLayout, setAutoLayout] = useState(true)
  const [isExporting, setIsExporting] = useState(false)
  const flowRef = useRef<TChatbotPreviewFlowHandle>(null)
  const orientation = selectedOrientation.value as TPdfOrientation

  const previewNodes = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          selectedLanguage: selectedLanguage.value as TPatientLocale,
        },
      })),
    [nodes, selectedLanguage],
  )

  const runExport = async (exportFn: () => Promise<void>) => {
    setIsExporting(true)
    try {
      await exportFn()
    } catch {
      notify({
        title: t('pages.configuration.followups.chatbot.preview.pdf-export-error-title'),
        message: t('pages.configuration.followups.chatbot.preview.pdf-export-error-message'),
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    } finally {
      setIsExporting(false)
    }
  }

  const pdfExportItems = [
    {
      label: t('pages.configuration.followups.chatbot.preview.download-pdf-preview-auto'),
      onClick: () => runExport(async () => flowRef.current?.exportPdfPreview({ orientation })),
    },
    {
      label: t('pages.configuration.followups.chatbot.preview.download-pdf-preview-normal'),
      onClick: () =>
        runExport(async () => flowRef.current?.exportPdfPreview({ zoomOverride: 1, orientation })),
    },
    {
      label: t('pages.configuration.followups.chatbot.preview.download-pdf-preview-large'),
      onClick: () =>
        runExport(async () => flowRef.current?.exportPdfPreview({ zoomOverride: 1.5, orientation })),
    },
    {
      label: t('pages.configuration.followups.chatbot.preview.download-pdf-grid'),
      onClick: () => runExport(async () => flowRef.current?.exportPdfGrid({ orientation })),
    },
  ]

  return (
    <CurecallModalContainer
      isOpen
      toggle={onClose}
      title={t('pages.configuration.followups.chatbot.preview.modal-title', { chatbotName })}
      closeModalAriaLabel={t('pages.configuration.followups.chatbot.preview.close')}
      dialogClassName="c-w-[95vw] c-max-w-[1600px] [&>div]:c-flex [&>div]:c-flex-col c-min-h-[95vh] !c-top-[28px]"
      bodyClassName="c-flex-1 c-flex c-flex-col c-overflow-hidden"
    >
      <div className="c-flex c-flex-col c-h-full">
        <div className="c-mb-3 c-flex c-gap-2 c-items-center">
          <p className="c-text-sm c-text-gray-600">
            {t('pages.configuration.followups.chatbot.preview.modal-subtitle')}
          </p>
          <div className="c-ml-auto c-flex c-gap-2 c-items-center">
            <label className="c-flex c-items-center c-gap-2 c-text-sm c-text-gray-700 c-cursor-pointer">
              <CurecallToggle isEnabled={autoLayout} onChange={setAutoLayout} />
              {t('pages.configuration.followups.chatbot.preview.auto-layout')}
            </label>
            <CurecallSelect
              items={languageOptions}
              selectedItem={selectedLanguage}
              onChange={setSelectedLanguage}
              appearance={ECurecallSelectAppearance.TEXT}
              IconLeft={<GlobeIcon className="tw-h-4 tw-w-4 tw-text-primary" />}
            />
            <CurecallSelect
              items={orientationOptions}
              selectedItem={selectedOrientation}
              onChange={setSelectedOrientation}
              appearance={ECurecallSelectAppearance.TEXT}
            />
            <CurecallDropdownButton
              dropdownButtonLabel={t('pages.configuration.followups.chatbot.preview.download-pdf')}
              items={pdfExportItems}
              dropDownButtonAppearance={ECurecallButtonAppearance.PRIMARY}
              isDisabled={isExporting}
            />
          </div>
        </div>
        <ReactFlowProvider>
          <ChatbotPreviewFlow
            ref={flowRef}
            previewNodes={previewNodes}
            edges={edges}
            chatbotName={chatbotName}
            followupName={followupName}
            autoLayout={autoLayout}
          />
        </ReactFlowProvider>
        <div className="c-mt-3 c-flex c-items-center c-gap-4 c-text-sm c-text-gray-800">
          <span className="c-font-semibold">
            {t('pages.configuration.followups.chatbot.preview.pdf-legend')} :
          </span>
          <span className="c-inline-block c-px-2 c-py-1 c-rounded c-border c-border-pink-600 c-bg-pink-200 c-text-pink-700 c-font-bold">
            <AlertIcon className="c-h-3 c-w-3 c-inline-block c-mr-1" />
            {t('pages.configuration.followups.chatbot.preview.pdf-alert-response')}
          </span>
          <span className="c-inline-block c-px-2 c-py-1 c-rounded c-border c-border-gray-400 c-bg-white c-text-gray-800 c-font-bold">
            {t('pages.configuration.followups.chatbot.preview.pdf-normal-response')}
          </span>
          <span className="c-inline-block c-px-2 c-py-1 c-rounded c-border c-border-gray-700 c-bg-gray-50 c-font-bold c-border-l-2">
            {t('pages.configuration.followups.chatbot.preview.pdf-content')}
          </span>
        </div>
      </div>
    </CurecallModalContainer>
  )
}
